import type { ApiBody, ApiResponse } from '../../api/contract';

type RunResponse = ApiResponse<'/api/v1/reconciliation-runs/{id}', 'get'>;
type ExceptionResponse = ApiResponse<'/api/v1/reconciliation-exceptions/{id}', 'get'>;

export type ReconciliationRun = RunResponse['data'];
export type ReconciliationRunDetail = ReconciliationRun;
export type ReconciliationException = ExceptionResponse['data'];
export type ReconciliationExceptionDetail = ReconciliationException;
export type ReconciliationType = ReconciliationRun['type'];
export type ReconciliationTrigger = ReconciliationRun['trigger'];
export type ReconciliationRunPhase = ReconciliationRun['phase'];
export type ReconciliationExceptionStatus = ReconciliationException['status'];
export type ReconciliationExceptionSeverity = ReconciliationException['severity'];
export type CreateReconciliationRunInput = ApiBody<'/api/v1/reconciliation-runs', 'post'>;
export type ResolveExceptionInput = ApiBody<
  '/api/v1/reconciliation-exceptions/{id}/resolve',
  'post'
>;
export type AcknowledgeExceptionInput = ApiBody<
  '/api/v1/reconciliation-exceptions/{id}/acknowledge',
  'post'
>;

export interface ReconciliationRunFilters {
  readonly type: ReconciliationType | '';
  readonly trigger: ReconciliationTrigger | '';
  readonly phase: ReconciliationRunPhase | '';
  readonly partnerId: string;
  readonly requestedFrom: string;
  readonly requestedTo: string;
}

export interface ReconciliationRunQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly filters: ReconciliationRunFilters;
}

export interface ReconciliationExceptionFilters {
  readonly status: ReconciliationExceptionStatus | '';
  readonly severity: ReconciliationExceptionSeverity | '';
  readonly type: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly partnerId: string;
  readonly detectedFrom: string;
  readonly detectedTo: string;
}

export interface ReconciliationExceptionQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly filters: ReconciliationExceptionFilters;
}

export interface ReconciliationEvidence {
  readonly sourceTotal: string | null;
  readonly internalTotal: string | null;
  readonly difference: string | null;
  readonly differenceCurrency: string | null;
  readonly sourceAmount: string | null;
  readonly internalAmount: string | null;
  readonly sourceCurrency: string | null;
  readonly internalCurrency: string | null;
  readonly duplicateIds: readonly string[];
  readonly missingReceipts: readonly string[];
  readonly staleReservations: readonly string[];
  readonly evidence: readonly string[];
}

const decimalPattern = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/u;

export function reconciliationEvidence(exception: ReconciliationException): ReconciliationEvidence {
  const expected = exception.expected ?? {};
  const actual = exception.actual ?? {};
  const expectedAmount = safeDecimal(expected['amount']);
  const actualAmount = safeDecimal(actual['amount']);
  const sourceCurrency = safeCode(expected['currency']);
  const internalCurrency = safeCode(actual['currency']);
  const explicitDifferenceCurrency =
    safeCode(expected['differenceCurrency']) ?? safeCode(actual['differenceCurrency']);
  return {
    sourceTotal: safeDecimal(expected['sourceTotal']) ?? expectedAmount,
    internalTotal: safeDecimal(actual['internalTotal']) ?? actualAmount,
    difference: safeDecimal(expected['difference']) ?? safeDecimal(actual['difference']),
    differenceCurrency:
      explicitDifferenceCurrency ??
      (sourceCurrency !== null && sourceCurrency === internalCurrency ? sourceCurrency : null),
    sourceAmount: expectedAmount,
    internalAmount: actualAmount,
    sourceCurrency,
    internalCurrency,
    duplicateIds: safeStrings(expected['duplicateIds'], actual['duplicateIds']),
    missingReceipts: safeStrings(expected['missingReceipts'], actual['missingReceipts']),
    staleReservations: safeStrings(expected['staleReservations'], actual['staleReservations']),
    evidence: safeStrings(expected['evidence'], actual['evidence']),
  };
}

export const RESOLUTION_REASON_MAX_LENGTH = 2000;

export function composeResolutionReason(evidence: string, reason: string): string {
  const combined = `Evidence: ${evidence.trim()}\nReason: ${reason.trim()}`;
  if (combined.length > RESOLUTION_REASON_MAX_LENGTH)
    throw new RangeError('Combined evidence and reason must be 2,000 characters or fewer');
  return combined;
}

export function formatExactMoney(amount: string, currency: string): string {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(amount.trim());
  if (match === null) return `${currency.trim().toUpperCase()}\u00a0${amount}`;
  const whole = match[2] ?? '0';
  const fraction = (match[3] ?? '').padEnd(2, '0');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
  return `${match[1] ?? ''}${currency.trim().toUpperCase()}\u00a0${grouped}.${fraction}`;
}

function safeDecimal(value: unknown): string | null {
  return typeof value === 'string' && decimalPattern.test(value) ? value : null;
}

function safeCode(value: unknown): string | null {
  return typeof value === 'string' && /^[A-Z][A-Z0-9_]{1,99}$/u.test(value) ? value : null;
}

function safeStrings(...values: readonly unknown[]): readonly string[] {
  return values.flatMap((value) =>
    Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string').slice(0, 100)
      : [],
  );
}
